import { createApp } from 'vue';
import * as Sentry from '@sentry/vue';

// ponytail: crypto.randomUUID absent in older mobile WebKit (DDG browser, iOS < 15.4)
if (!crypto.randomUUID) {
  crypto.randomUUID = () =>
    (([1e7] as any) + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
    );
}
import { createI18n } from 'vue-i18n';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import './assets/shared.css';
import App from './App.vue';
import { router } from './router/index.js';
import { registerSW } from 'virtual:pwa-register';
import en from './locales/en.js';
import es from './locales/es.js';
import { initAppNavigation } from '@/composables/useAppNavigation.js';

registerSW({ immediate: true });

const i18n = createI18n({
  fallbackLocale: 'en',
  legacy: false,
  locale: localStorage.getItem('locale') ?? 'en',
  messages: { en, es },
});

const app = createApp(App).use(router).use(i18n);

// Initialize global navigation helpers
initAppNavigation(router);

// Native builds hand control back to the app via the `punchlog://` custom URL scheme after
// Stripe Checkout/Portal (opened in the system browser -- see useSubscription.ts's planUrl()),
// registered in AndroidManifest.xml and ios/App/App/Info.plist. Only "settings/plan" is ever
// produced today, but parse generically rather than hardcoding that one path.
if (Capacitor.isNativePlatform()) {
  CapacitorApp.addListener('appUrlOpen', ({ url }) => {
    const parsed = new URL(url);
    if (parsed.host !== 'settings') return;
    const tab = parsed.pathname.replace(/^\//, '') || undefined;
    const checkout = parsed.searchParams.get('checkout');
    router.push({
      name: 'settings',
      params: { tab },
      query: checkout ? { checkout } : {},
    });
  });
}

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration({ router })],
  // Zero-knowledge: no user PII, no request bodies
  sendDefaultPii: false,
  tracesSampleRate: 0.2,
});

if (import.meta.env.DEV) {
  const { VueAxe, VueAxePopup } = await import('vue-axe');
  app.use(VueAxe);
  app.component('VueAxePopup', VueAxePopup);
}

app.mount('#app');
